import {useQuery, type QueryKey} from '@tanstack/react-query'
import type {Page} from '@/types/page'

interface UseOpenRequestGuardOptions<T> {
    queryKey: QueryKey
    queryFn: () => Promise<Page<T>>
    enabled?: boolean
}

interface UseOpenRequestGuardResult<T> {
    openRequest: T | null
    hasOpenRequest: boolean
}

/**
 * Shared hook for detail pages that guard against concurrent change requests.
 *
 * Runs the supplied query (which should fetch DRAFT/PENDING requests for a rule,
 * with `size: 1`) and returns the first result, if any. Callers own the query
 * key and fetcher so the hook is service-agnostic.
 */
export function useOpenRequestGuard<T>({
    queryKey,
    queryFn,
    enabled = true,
}: UseOpenRequestGuardOptions<T>): UseOpenRequestGuardResult<T> {
    const {data} = useQuery({
        queryKey,
        queryFn,
        enabled,
    })
    const openRequest = data?.content?.[0] ?? null
    return {openRequest, hasOpenRequest: !!openRequest}
}
